import { useEffect, useMemo, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";

import Container from "@mui/material/Container";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";

import PageLayout from "examples/LayoutContainers/PageLayout";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import config from "../../../config";

function useQuery() {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
}

export default function RegisterSuccess() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const query = useQuery();
    const pa = query.get("pa");

    const statusRef = useRef("PROCESSING");

    const [status, setStatus] = useState("PROCESSING"); // PROCESSING | ACTIVE | UNKNOWN
    const [error, setError] = useState("");

    useEffect(() => {
        statusRef.current = status;
    }, [status]);


    useEffect(() => {
        if (!pa) {
            setStatus("UNKNOWN");
            return;
        }

        let cancelled = false;
        let timeoutId = null;
        let tries = 0;

        const poll = async () => {
            try {
                const res = await axios.get(
                    `${config.BACKEND_BASE_URL}/api/admin/register/onboarding/status`,
                    { params: { pa } }
                );
                if (cancelled) return;

                const s = res.data?.status;

                if (s === "ACTIVE") {
                    setStatus("ACTIVE");
                    statusRef.current = "ACTIVE";
                    return;
                }

                setStatus("PROCESSING");
                statusRef.current = "PROCESSING";
            } catch (e) {
                if (cancelled) return;
                setError(e?.response?.data?.message || t("register.success.confirmError"));
                setStatus("PROCESSING");
                statusRef.current = "PROCESSING";
            } finally {
                tries += 1;
                if (!cancelled && statusRef.current !== "ACTIVE" && tries < 20) {
                    timeoutId = setTimeout(poll, 3000);
                }
            }
        };

        poll();

        return () => {
            cancelled = true;
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [pa]);

    const isActive = status === "ACTIVE";

    return (
        <PageLayout>
            <MDBox minHeight="60vh" display="flex" alignItems="center" py={6}>
                <Container>
                    <Grid container justifyContent="center">
                        <Grid item xs={12} sm={10} md={7} lg={6}>
                            <Card>
                                <MDBox p={4} textAlign="center">
                                    <MDTypography variant="h4" fontWeight="bold" mb={1}>
                                        {t("register.success.paymentComplete")}
                                    </MDTypography>

                                    <MDTypography variant="button" color="text" mb={3} display="block">
                                        {isActive
                                            ? t("register.success.accountReady")
                                            : t("register.success.activating")}
                                    </MDTypography>

                                    {!isActive ? (
                                        <MDBox display="flex" justifyContent="center" my={2}>
                                            <CircularProgress />
                                        </MDBox>
                                    ) : null}

                                    {error ? (
                                        <MDBox mt={2}>
                                            <MDTypography variant="button" color="warning">
                                                {error}
                                            </MDTypography>
                                        </MDBox>
                                    ) : null}

                                    <MDBox mt={4} display="flex" gap={1.5} justifyContent="center" flexWrap="wrap">
                                        <MDButton
                                            variant="gradient"
                                            color="info"
                                            onClick={() => navigate("/authentication/sign-in")}
                                        >
                                            {t("register.success.goToSignIn")}
                                        </MDButton>

                                        <MDButton variant="outlined" color="dark" onClick={() => navigate("/authentication/register")}>
                                            {t("register.success.backToPackages")}
                                        </MDButton>
                                    </MDBox>

                                    <MDBox mt={3}>
                                        <MDTypography variant="caption" color="text">
                                            {t("register.success.checkSpam")}
                                        </MDTypography>
                                    </MDBox>
                                </MDBox>
                            </Card>
                        </Grid>
                    </Grid>
                </Container>
            </MDBox>
        </PageLayout>
    );
}
